/**
 * 全局 Prompt 注册表（P2 · 逐环节 prompt 补全的实现层）。
 *
 * 唯一依据：docs/全局Prompt规范.md。这里把旧版 prompt_enhancer 8 层 + 旧版剧本/分镜
 * system prompt + 竞品保质量护栏（分层/字数阈值/负面两层/时长夹取/导演式具体化）
 * 沉淀成常量与纯函数。mock 阶段 compose 走 buildKeyframePrompt/buildVideoPrompt；
 * 接真实 LLM 时 provider 直接取 *_SYSTEM 作 system prompt，compose 产物作用户 prompt。
 */
import type { StoryboardShot } from './generation';

/* ---------- 护栏阈值（规范 §一 G3/G7） ---------- */

/** 单条分镜/关键帧图像 prompt 上限（分层后；每维≤3 描述符，超出先丢次要层）。 */
export const SHOT_LENGTH_LIMIT = 400;
/** 分镜视频 prompt 安全上限（对齐旧版 prompt_enhancer）。 */
export const VIDEO_LENGTH_LIMIT = 3000;
/** 单镜视频时长夹取区间（秒）——多数模型 8–10s 后特征漂移。 */
export const CLIP_DURATION_SEC: readonly [number, number] = [5, 10];

/* ---------- 负面提示词两层（规范 §二 G4） ---------- */

/** 通用伪影层：所有图像/关键帧/视频节点默认带上。 */
export const DEFAULT_NEGATIVE =
  '低分辨率，模糊，JPEG压缩痕迹，噪点，过曝，变形，面部扭曲，毁容，肢体畸形，' +
  '多余的手指，手指计数错误，手部粘连，比例错误，姿势走样，文字，水印，logo，字幕，' +
  '裁切不当，画面闪烁，抖动，穿模，塑胶感，油腻皮肤，呆板表情，眼神涣散';

/** 场景特定负向层（规范 §二）：只写"不要什么"，不与正向矛盾。 */
export function sceneNegative(kind: 'empty-scene' | 'night' | 'indoor' | 'closeup'): string {
  switch (kind) {
    case 'empty-scene':
      return '多余人物，路人，背景人影，肢体，面部';
    case 'night':
      return '白天光线，过曝高光，不自然日光';
    case 'indoor':
      return '窗外不合理强光，穿墙光线';
    case 'closeup':
      return '多余身体部位，画面裁切到额头，画面裁切到下巴';
  }
}

/* ---------- 一致性规则（规范 §一 G8，供角色卡 prompt 复用固定特征词） ---------- */

export const CONSISTENCY_RULE =
  '保持同一角色的五官/发型/体型/标志物与当前造型完全一致；跨镜头/跨集不跑色；参考图权重 0.65–0.80，以首帧锁定外观。';

/* ---------- 逐环节 System Prompt（规范 §三，接真 LLM 直接取用） ---------- */

/** 文学剧本创作 system prompt（旧版 script_routes 蒸馏）。 */
export const LITERARY_SYSTEM = [
  '你是一位拥有三十年经验的文学大师，擅长人物刻画细腻、场景描写生动、情感丰富、叙事节奏紧凑的短剧文学剧本。',
  '必含：①人物首次登场 80–150 字外貌+穿着+神态+标志性特征，换装必写新服装与原因；',
  '②每个场景写空间结构/光影/色彩基调/2-3 个标志物/一句话氛围；③动作有细节、肢体语言、环境互动；',
  '④**台词密度要高**：对白近乎贯穿全剧，占比≥40%，每句都有功能（推动剧情/揭示性格/埋伏笔），充满潜台词、个性化、无废话；',
  '⑥开篇 200 字内建立核心冲突或悬念，全文≥3 次转折，伏笔呼应，结尾留余韵。',
  '输出：完整文学剧本纯文本，不加 JSON、标题、元数据或注释。',
].join('\n');

/** 导演分镜 system prompt（旧版 script_routes 蒸馏 + 最高优先级护栏）。 */
export const STORYBOARD_SYSTEM = [
  '你是资深影视创作大师，集金牌编剧、视觉导演、摄影指导于一身。',
  '⚠️ 最高优先级：严禁精简/概括/合并。逐句把文学剧本转换为导演分镜，每个关键动作/情绪/场景切换独立成镜；总内容不少于文学剧本 80%。',
  '每镜必填（具象、可直接喂视频模型）：scene_description(80-150)、lighting(50-100，光源/方向/色温/阴影/质感)、',
  'action(150-300，动作+神态+手部+眼神+重心+环境互动+服装动态，抽象情绪翻译成 2-3 个可观察物理特征)、',
  'character_visual(每人 80-150，当前造型款式/颜色/面料/配饰+表情+体态)、emotion_atmosphere(50-100)、',
  'dialogue(角色名: 台词，逐字保留不删改)、voice_direction(30-60，语调/语速/情感)、shot_type(景别)、',
  'camera(50-100，运镜类型+完整轨迹+景深，运镜与动作分开写)、sound_design(50-100，环境音+BGM)、duration(2-30s)、transition、props(出场道具)。',
  '角色：所有角色 gender/age 必填(未写明据外貌推断)；visual_style≥80 字；looks[] 拆出全部造型(名+描述50-100+场景)，禁止空数组。',
  '节奏：开篇黄金 3 秒建冲突，每集 3-5 次反转，结尾留钩子。',
  '❗台词密度（短剧命脉）：对白/内心独白/画外音几乎贯穿每一镜，连续无对白镜头不超过 1 个；靠大量台词衬托角色、推动节奏。',
  '输出：严格 JSON（title/characters[]/scenes[]/shots[] 见项目 schema），不含多余文本。',
].join('\n');

/* ---------- compose 分层拼装（规范 §3.6 / §3.8） ---------- */

function clampLen(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** 时长夹进 [5,10]（G7），无有效值回退 5。 */
export function clampClipDuration(sec: number): number {
  if (!Number.isFinite(sec) || sec <= 0) {
    return CLIP_DURATION_SEC[0];
  }
  return Math.max(CLIP_DURATION_SEC[0], Math.min(CLIP_DURATION_SEC[1], Math.round(sec)));
}

export interface ShotPromptCtx {
  /** 出场角色参考图编号名（图N），供一致性引用 */
  characterRefs?: string[] | undefined;
  /** 是否已有首帧关键帧（图生视频） */
  hasKeyframe?: boolean | undefined;
}

/** 分镜关键帧 prompt：环境/主体造型/动作/构图/光影/色彩/情绪/质感 分层，含长度护栏。 */
export function buildKeyframePrompt(shot: StoryboardShot, ctx: ShotPromptCtx = {}): string {
  const chars = ctx.characterRefs !== undefined && ctx.characterRefs.length > 0
    ? ctx.characterRefs.join('、')
    : shot.characters.join('、');
  const env = [shot.scene, shot.scene_description].filter((s) => s !== '').join('，');
  const subject = [chars, shot.character_visual].filter((s) => s !== '').join('；');
  const layers = [
    env !== '' ? `环境：${env}` : '',
    subject !== '' ? `主体：${subject}` : '',
    `动作：${shot.action}`,
    `构图：景别${shot.shotSize}，机位${shot.cameraAngle}`,
    `光影：${shot.lighting}`,
    `色彩：${shot.colorTone}`,
    `情绪：${shot.emotion}`,
    '质感：电影级、8K、真实光影、构图专业',
  ].filter((l) => l !== '' && !/[：:]\s*$/.test(l));
  return clampLen(layers.join('。'), SHOT_LENGTH_LIMIT);
}

/** 分镜视频 prompt：对齐旧版 8 层（含场景描述/角色视觉/音效/音色），运镜与动作分离，≤3000。 */
export function buildVideoPrompt(shot: StoryboardShot, ctx: ShotPromptCtx = {}): string {
  const chars = ctx.characterRefs !== undefined && ctx.characterRefs.length > 0 ? ctx.characterRefs : shot.characters;
  const role = [chars.join('、'), shot.character_visual].filter((s) => s !== '').join('；')
    + (shot.props.length > 0 ? `，关键道具：${shot.props.join('、')}` : '');
  const scene = [shot.scene, shot.scene_description].filter((s) => s !== '').join('，');
  const audio = [
    shot.sound_design !== '' ? `环境音：${shot.sound_design}` : '',
    shot.dialogue !== '' ? `台词「${shot.dialogue}」` : '',
    shot.voice_direction !== '' ? `音色：${shot.voice_direction}` : '',
  ].filter((s) => s !== '').join('；');
  const layers = [
    `【场景】${scene}`,
    `【光影】${shot.lighting}`,
    `【角色】${role}`,
    `【动作】${shot.action}`,
    `【情绪】${shot.emotion}`,
    `【运镜】${shot.shotSize}·${shot.cameraAngle}·${shot.cameraMove}${shot.transition !== '' ? `，转场${shot.transition}` : ''}`,
    `【音频】${audio !== '' ? audio : '环境音'}`,
    `【画质】电影级、${clampClipDuration(shot.durationSec)}秒${ctx.hasKeyframe === true ? '、以首帧为基准' : ''}`,
  ];
  return clampLen(layers.filter((l) => !/[：:]\s*$/.test(l)).join('\n'), VIDEO_LENGTH_LIMIT);
}
